import { Box, Flex, Spinner, Text } from "@chakra-ui/react";
import CompletedCourse from "../../mylearning/completed-course";
import { useMyLearningContext } from "@root/context/my-learning-context";
import { useTranslation } from "react-i18next";
import { Paginate } from "@root/components/pagination";
import { RECORDS_PER_PAGE, COURSE_ENROLLMENT_STATUS } from "@root/constants";
import { useMemo, useEffect, useState } from "react";
import { MyLearningService } from "@root/services/my-learning";

const CompletedTab = () => {
  const { t } = useTranslation("common");
  const {
    getMyLearningLoading,
    myLearningData,
    pageNo,
    setPageNo,
    pageCount,
    fetchMyLearning,
  } = useMyLearningContext();
  
  const [enrolledCoursesData, setEnrolledCoursesData] = useState<any>(null);
  const [enrolledCoursesLoading, setEnrolledCoursesLoading] = useState(false);
  const myLearningService = new MyLearningService();

  // Fetch enrolled courses to check for expired enrollment courses with 100% progress
  useEffect(() => {
    const fetchEnrolledCourses = async () => {
      setEnrolledCoursesLoading(true);
      try {
        const payload = {
          status: COURSE_ENROLLMENT_STATUS.ENROLLED,
          limit: 1000, // Fetch a large number to get all enrolled courses
          offset: 0,
        };
        const res = await myLearningService.fetchMyLearning(payload);
        setEnrolledCoursesData(res?.data?.data);
      } catch (error) {
        console.warn("Error fetching enrolled courses:", error);
      } finally {
        setEnrolledCoursesLoading(false);
      }
    };
    fetchEnrolledCourses();
  }, []);

  const handlePageChange = (newPage: number) => {
    setPageNo(newPage);
    fetchMyLearning(
      { status: COURSE_ENROLLMENT_STATUS.COMPLETED },
      newPage
    );
  };

  // Merge completed courses with expired enrollment courses that have 100% progress
  const mergedCourses = useMemo(() => {
    const completedCourses = myLearningData?.rows || [];
    const enrolledCourses = enrolledCoursesData?.rows || [];
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Find enrolled courses with expired enrollment and 100% progress
    const expiredEnrolledWithFullProgress = enrolledCourses.filter((course: any) => {
      const courseData = course?.course;
      const enrollmentPeriodEnd = courseData?.enrollmentPeriodEnd;
      
      if (!enrollmentPeriodEnd) return false;
      
      const endDate = new Date(enrollmentPeriodEnd);
      endDate.setHours(0, 0, 0, 0);
      
      // Check if enrollment period has ended
      if (today <= endDate) return false;
      
      // Check if progress is 100%
      const progress = typeof course?.progress === "number"
        ? course.progress
        : parseFloat(course?.progress || "0");
      
      return progress >= 100;
    });
    
    // Merge and deduplicate by course ID
    const courseMap = new Map();
    
    // Add completed courses
    completedCourses.forEach((course: any) => {
      const courseId = course?.course?.id;
      if (courseId) {
        courseMap.set(courseId, course);
      }
    });
    
    // Add expired enrolled courses with full progress
    expiredEnrolledWithFullProgress.forEach((course: any) => {
      const courseId = course?.course?.id;
      if (courseId && !courseMap.has(courseId)) {
        courseMap.set(courseId, course);
      }
    });
    
    return Array.from(courseMap.values());
  }, [myLearningData?.rows, enrolledCoursesData?.rows]);

  if (getMyLearningLoading || enrolledCoursesLoading) {
    return <Spinner size={"lg"} mt={"150px"} />;
  }
  if (!mergedCourses?.length) {
    return (
      <Text color={"#5C6670"} mt={"150px"}>
        {t("course.noDataFound")}
      </Text>
    );
  }
  return (
    <Box w="100%" gap="16px" display="flex" flexDirection="column">
      {mergedCourses.map((course: any, idx: any) => (
        <CompletedCourse
          key={idx}
          course={course?.course}
          completedAt={course?.completedAt}
        />
      ))}
      {/* Pagination */}
      {pageCount > 0 && (
        <Box mt={6} display="flex" justifyContent="center">
          <Paginate
            marginTop={0}
            page={pageNo}
            count={pageCount}
            pageSize={RECORDS_PER_PAGE}
            onPageChange={handlePageChange}
            variant="ghost"
          />
        </Box>
      )}
    </Box>
  );
};

export default CompletedTab;
